/**
 * openWishlistProductConfig(productHandle, callback)
 *
 * Fetches ?view=wishlist-config for the product, injects the HTML into a
 * singleton drawer, wires up WishlistSqftWidget, then calls callback(data)
 * when the user clicks "Save to Wishlist".
 *
 * callback receives: { variantId, quantity, properties }
 */

(function () {
  "use strict";

  console.log("[WL] wishlist-product-config.js loaded");

  /* ------------------------------------------------------------------ */
  /* Drawer DOM (pre-rendered via wishlist-drawer-shell.liquid)          */
  /* ------------------------------------------------------------------ */

  // Inject keyframe into <head> so the spinner animation is always available
  // independently of when sqft-calculator.css finishes loading.
  (function () {
    if (document.getElementById("wl-spin-kf")) return;
    const s = document.createElement("style");
    s.id = "wl-spin-kf";
    s.textContent = "@keyframes wl-spin{to{transform:rotate(360deg)}}";
    document.head.appendChild(s);
  })();

  const SPINNER_HTML =
    '<div class="wl-drawer-loading"><div class="wl-drawer-spinner"></div></div>';

  // Script is deferred — the shell elements are in the DOM by the time this runs.
  const drawerEl = document.getElementById("wl-config-drawer");
  const overlayEl = document.getElementById("wl-config-overlay");
  if (overlayEl) overlayEl.addEventListener("click", closeDrawer);

  function openDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.add("is--active");
    overlayEl.classList.add("is--active");
    document.body.style.overflow = "hidden";

    if (window.innerWidth > 750) {
      const header = document.querySelector(".section-header");
      const announce = document.querySelector(".section-announcement-bar");
      if (header) header.style.zIndex = "";
      if (announce) announce.style.zIndex = "";
    }
  }

  function closeDrawer() {
    if (!drawerEl) return;
    drawerEl.classList.remove("is--active");
    overlayEl.classList.remove("is--active");
    document.body.style.overflow = "";
    setTimeout(() => {
      if (!drawerEl.classList.contains("is--active")) {
        drawerEl.innerHTML = SPINNER_HTML;
      }
    }, 500);
  }

  /* ------------------------------------------------------------------ */
  /* CSS guard                                                            */
  /* ------------------------------------------------------------------ */

  function ensureCss(href) {
    if (!href || document.querySelector(`link[href="${href}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = href;
    document.head.appendChild(link);
  }

  /* ------------------------------------------------------------------ */
  /* Utilities                                                            */
  /* ------------------------------------------------------------------ */

  function debounce(fn, ms) {
    let t;
    return function (...args) {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), ms);
    };
  }

  class WishlistSqftWidget {
    constructor(container, productUrl, callback) {
      this.container = container;
      this.productUrl = productUrl;
      this.callback = callback;

      // All options JSON — JS detects sqft option by normalising name
      const allOptionsEl = container.querySelector("#wishlist_all_options");
      const allOptions = allOptionsEl
        ? JSON.parse(allOptionsEl.textContent)
        : [];
      const sqftOption = allOptions.find((opt) => {
        const norm = opt.name.toLowerCase().replace(/[\s.]/g, "");
        return norm === "sqft" || norm === "inches";
      });
      this.optionValues = sqftOption ? sqftOption.values : [];

      // Texture variants — keyed by texture name, sqft-indexed variants
      const textureVariantsEl = container.querySelector(
        "#wishlist_texture_variants",
      );
      this.textureVariants = textureVariantsEl
        ? JSON.parse(textureVariantsEl.textContent || "{}")
        : {};

      const widget = container.querySelector(".wishlist-sqft-widget");
      this.isEmbrWallArtCustom = widget?.dataset.isEmbrWallArtCustom === "true";

      this.el = {
        widthInput: container.querySelector("#wl-width"),
        heightInput: container.querySelector("#wl-height"),
        unitRadios: container.querySelectorAll('input[name="wl-unit"]'),
        textureRadios: container.querySelectorAll(
          'input[name="wl-product-texture"]',
        ),
        sqftDisplay: container.querySelector(".wl-sqft-display"),
        sqftFooter: container.querySelector(".wl-sqft-footer-display"),
        priceDisplay: container.querySelector(".wl-price-display"),
        cardHeader: container.querySelector(".wl-card-header"),
        warningMsg: container.querySelector(".wl-card-warning"),
        errorMsg: container.querySelector(".wl-error-message"),
        errorText: container.querySelector(".wl-error-text"),
        addBtn: container.querySelector("#wl-add-to-wishlist-btn"),
        closeBtn: container.querySelector(".wl-drawer-close"),
        quantityDisplay: container.querySelector("#wl-quantity-display"),
        decreaseBtn: container.querySelector("#wl-quantity-decrease-btn"),
        increaseBtn: container.querySelector("#wl-quantity-increase-btn"),
      };

      const initialTexture = container.querySelector(
        'input[name="wl-product-texture"]:checked',
      );

      this.state = {
        unit: "in",
        quantity: 1,
        baseSqft: 0,
        basePrice: 0,
        variantId: null,
        closestOptionId: null,
        texture: initialTexture?.value || null,
        textureVariantId: initialTexture?.dataset.variantId || null,
        texturePrice: parseFloat(initialTexture?.dataset.price || 0),
        isTextureVariantMatched: false,
        hasError: false,
      };

      this.conversionToFeet = { in: 1 / 12, mm: 0.00328084 };
      this._debouncedRenderPrice = debounce(this._renderPrice.bind(this), 500);

      this._init();
      this._calculate();
    }

    /* ---- Event listeners --------------------------------------------- */

    _init() {
      const {
        widthInput,
        heightInput,
        unitRadios,
        textureRadios,
        addBtn,
        closeBtn,
        decreaseBtn,
        increaseBtn,
      } = this.el;

      const intOnly = (e) => {
        const pass = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
        if (!pass.includes(e.key) && !/^[0-9]$/.test(e.key)) e.preventDefault();
      };

      widthInput?.addEventListener("keydown", intOnly);
      heightInput?.addEventListener("keydown", intOnly);
      widthInput?.addEventListener("input", () => this._calculate());
      heightInput?.addEventListener("input", () => this._calculate());

      unitRadios.forEach((r) =>
        r.addEventListener("change", (e) => {
          this.state.unit = e.target.value;
          this._calculate();
        }),
      );

      textureRadios.forEach((r) =>
        r.addEventListener("change", (e) => {
          this.state.texture = e.target.value;
          this._updateTexturePrice();
          this._renderUI();
        }),
      );

      // const checkedTexture = this.container.querySelector(
      //   'input[name="wl-product-texture"]:checked',
      // );
      // if (checkedTexture) this.state.texture = checkedTexture.value;

      // Non-sqft option radios (Color, Material, etc.)
      // Re-fetch only after sqft is resolved — otherwise closestOptionId is null
      // and the API would receive only the color ID without a sqft option ID.
      const optionRadios = this.container.querySelectorAll(
        'input[name^="wl-option-"]',
      );
      optionRadios.forEach((r) =>
        r.addEventListener("change", (e) => {
          const pos = e.target.name.replace("wl-option-", "");
          const label = this.container.querySelector(`.wl-option-label-${pos}`);
          if (label) label.textContent = e.target.value;
          if (this.state.closestOptionId) this._debouncedRenderPrice();
        }),
      );

      decreaseBtn?.addEventListener("click", () => this._updateQuantity(-1));
      increaseBtn?.addEventListener("click", () => this._updateQuantity(1));
      closeBtn?.addEventListener("click", () => closeDrawer());
      addBtn?.addEventListener("click", () => this._submit());
    }

    /* ---- Quantity ---------------------------------------------------- */

    _updateQuantity(delta) {
      const newQty = this.state.quantity + delta;
      if (newQty >= 1) {
        this.state.quantity = newQty;
        this._renderUI();
      }
    }

    /* ---- Option ID helpers ------------------------------------------- */

    // IDs from all non-sqft option radios currently checked in the drawer
    _getAllSelectedOptionIds() {
      return Array.from(
        this.container.querySelectorAll('input[name^="wl-option-"]:checked'),
      )
        .map((r) => r.dataset.wlOptionId)
        .filter(Boolean);
    }

    /* ---- Calculation — mirrors sqft-calculator.js calculate() -------- */

    _calculate() {
      const h = parseInt(this.el.heightInput?.value, 10) || 0;
      const w = parseInt(this.el.widthInput?.value, 10) || 0;

      if (!h || !w) {
        this._resetUI();
        return;
      }

      if (this.isEmbrWallArtCustom) {
        this._applyEmbrMax(h, w);
        if (h > 57 && w > 57) {
          this._setError(
            "Only one dimension can exceed 57. Please set height or width to 57 or less.",
          );
          return;
        }
      }

      const areaInSqFeet =
        h *
        this.conversionToFeet[this.state.unit] *
        w *
        this.conversionToFeet[this.state.unit];

      let calculatedArea, baseSqft;
      if (this.isEmbrWallArtCustom) {
        calculatedArea = areaInSqFeet;
        baseSqft = Math.ceil(calculatedArea);
      } else {
        // 10% wastage, rounded down to nearest 5
        calculatedArea = areaInSqFeet * 1.1;
        baseSqft = Math.floor(calculatedArea / 5) * 5;
      }

      const min = +this.optionValues[0]?.value || 0;
      if (baseSqft < min) {
        baseSqft = calculatedArea;
        if (this.el.warningMsg) this.el.warningMsg.style.display = "block";
      } else {
        if (this.el.warningMsg) this.el.warningMsg.style.display = "none";
      }

      this.state.baseSqft = baseSqft;
      this._validateAndMatchOption();
      this._updateTexturePrice();
      this._updateAllTexturePrices();
      this._renderUI();
    }

    /* ---- Texture pricing — mirrors sqft-calculator.js updateTexturePrice() */

    _updateTexturePrice() {
      const selected = this.container.querySelector(
        'input[name="wl-product-texture"]:checked',
      );
      if (!selected) return;

      const variants = this.textureVariants[selected.value] || [];
      const currentSqft = parseFloat(this.state.baseSqft);

      let match = variants.find(
        (v) => parseFloat(v.value || 0) === currentSqft,
      );

      if (!match && variants.length > 0) {
        const firstVariant = variants[0];
        const lastVariant = variants[variants.length - 1];
        const minVal = parseFloat(firstVariant?.value || 0);
        const maxVal = parseFloat(lastVariant?.value || 0);

        if (currentSqft > 0 && currentSqft < minVal) {
          match = firstVariant;
        } else if (currentSqft > maxVal) {
          match = lastVariant;
        }
      }

      if (match) {
        this.state.textureVariantId = match.id;
        this.state.texturePrice = parseFloat(match.price);
        this.state.isTextureVariantMatched = true;
      } else {
        this.state.textureVariantId = selected.dataset.variantId || null;
        this.state.texturePrice = parseFloat(selected.dataset.price || 0);
        this.state.isTextureVariantMatched = false;
      }
    }

    /* ---- Per-texture price labels — mirrors updateAllTexturePrices() */

    _updateAllTexturePrices() {
      const currentSqft = parseFloat(this.state.baseSqft);
      const labels = this.container.querySelectorAll(".product-texture");
      const isInputEmpty =
        !this.el.heightInput?.value && !this.el.widthInput?.value;

      labels.forEach((label) => {
        const input = label.querySelector('input[name="wl-product-texture"]');
        if (!input) return;

        const priceDisplay = label.querySelector(".product-texture__price");
        if (!priceDisplay) return;

        if (isInputEmpty) {
          priceDisplay.style.display = "none";
          return;
        }

        const variants = this.textureVariants[input.value] || [];
        const firstVariant = variants[0];
        const lastVariant = variants[variants.length - 1];
        const minVal = parseFloat(firstVariant?.value || 0);
        const maxVal = parseFloat(lastVariant?.value || 0);

        let match = variants.find(
          (v) => parseFloat(v.value || 0) === currentSqft,
        );
        if (!match && currentSqft > 0) {
          if (currentSqft < minVal && firstVariant) match = firstVariant;
          else if (currentSqft > maxVal && lastVariant) match = lastVariant;
        }

        let price;
        if (match) {
          price = parseFloat(match.price);
        } else {
          const basePrice = parseFloat(input.dataset.price || 0);
          price = basePrice * currentSqft;
        }

        if (price > 0) {
          priceDisplay.style.display = "block";
          priceDisplay.innerHTML = `+  $${price.toLocaleString("en-US")}`;
        } else {
          priceDisplay.style.display = "none";
        }
      });
    }

    _applyEmbrMax(h, w) {
      const MAX = 57;
      if (w > MAX) this.el.heightInput?.setAttribute("max", String(MAX));
      else this.el.heightInput?.removeAttribute("max");
      if (h > MAX) this.el.widthInput?.setAttribute("max", String(MAX));
      else this.el.widthInput?.removeAttribute("max");
    }

    /* ---- Variant matching — mirrors sqft-calculator.js validateAndMatchOption() */

    _validateAndMatchOption() {
      if (!this.optionValues.length) return;

      const firstOption = this.optionValues[0];
      const min = +firstOption?.value || 0;
      const max =
        +this.optionValues[this.optionValues.length - 1]?.value || Infinity;

      if (this.state.baseSqft > max) {
        this._setError(`Area is too large. Maximum ${max} sq ft exceeded.`);
        return;
      }

      this._setError(null);

      let match;
      if (this.state.baseSqft < min) {
        match = firstOption;
      } else {
        // Exact match first; nearest-neighbour fallback for floating-point baseSqft
        match = this.optionValues.find((o) => +o.value === this.state.baseSqft);
        if (!match) {
          match = this.optionValues.reduce((prev, curr) =>
            Math.abs(+curr.value - this.state.baseSqft) <
            Math.abs(+prev.value - this.state.baseSqft)
              ? curr
              : prev,
          );
        }
      }

      if (match) {
        // Skip re-fetch only when the resolved sqft option hasn't changed
        // AND we already have a valid cached price.
        if (
          match.id &&
          this.state.closestOptionId === match.id &&
          this.state.basePrice > 0
        ) {
          return;
        }
        this.state.closestOptionId = match.id || null;
        this._debouncedRenderPrice();
      }
    }

    /* ---- Section Rendering API — mirrors sqft-calculator.js renderPrice() */

    async _renderPrice() {
      // Combine: non-sqft option IDs (color, material) + resolved sqft option ID
      const allOptionValues = [
        ...this._getAllSelectedOptionIds(),
        ...(this.state.closestOptionId ? [this.state.closestOptionId] : []),
      ];

      if (!allOptionValues.length) {
        console.warn("[WL] _renderPrice — no option values, skipping fetch");
        return;
      }

      try {
        const url = `${
          this.productUrl
        }?section_id=data-selected-variant&option_values=${allOptionValues.join(
          ",",
        )}`;
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const rawJson = doc.querySelector("[selected-variant]")?.textContent;
        const data = JSON.parse(rawJson || "{}");

        this.state.variantId = data.id || null;
        this.state.basePrice = (data.price || 0) / 100;
        this._renderUI();
      } catch (err) {
        console.error("[WL] renderPrice failed", err);
      }
    }

    /* ---- UI ---------------------------------------------------------- */

    _renderUI() {
      const totalSqft = parseFloat(
        (this.state.baseSqft * this.state.quantity).toFixed(2),
      );
      const sqftText = `${totalSqft} sq. ft.`;

      if (this.el.sqftDisplay) this.el.sqftDisplay.textContent = sqftText;
      if (this.el.sqftFooter) this.el.sqftFooter.textContent = sqftText;
      if (this.el.cardHeader)
        this.el.cardHeader.style.display = totalSqft > 0 ? "block" : "none";
      if (this.el.quantityDisplay)
        this.el.quantityDisplay.textContent = this.state.quantity;

      if (this.state.basePrice && this.el.priceDisplay) {
        const textureTotal = this.state.isTextureVariantMatched
          ? this.state.texturePrice || 0
          : this.state.baseSqft * (this.state.texturePrice || 0);
        const total =
          (this.state.basePrice + textureTotal) * this.state.quantity;
        this.el.priceDisplay.innerHTML = `$${total.toLocaleString("en-IN")}`;
      }

      // Enable button only when sqft is resolved and no active error
      if (this.el.addBtn && !this.state.hasError)
        this.el.addBtn.disabled = false;
    }

    _resetUI() {
      if (this.el.sqftDisplay) this.el.sqftDisplay.textContent = "0 sq. ft.";
      if (this.el.sqftFooter) this.el.sqftFooter.textContent = "0 sq. ft.";
      if (this.el.priceDisplay) this.el.priceDisplay.innerHTML = "$0";
      if (this.el.cardHeader) this.el.cardHeader.style.display = "none";
      if (this.el.warningMsg) this.el.warningMsg.style.display = "none";
      if (this.el.quantityDisplay)
        this.el.quantityDisplay.textContent = this.state.quantity;
      if (this.el.addBtn) this.el.addBtn.disabled = true;
      this.state.baseSqft = 0;
      this.state.basePrice = 0;
      this.state.closestOptionId = null;
      this._updateTexturePrice();
      this._updateAllTexturePrices();
      this._setError(null);
    }

    _setError(msg) {
      this.state.hasError = !!msg;
      if (this.el.errorMsg)
        this.el.errorMsg.style.display = msg ? "flex" : "none";
      if (this.el.errorText) this.el.errorText.textContent = msg || "";
      if (this.el.addBtn) this.el.addBtn.disabled = !!msg;
    }

    /* ---- Submit ------------------------------------------------------- */

    _submit() {
      const h = this.el.heightInput?.value;
      const w = this.el.widthInput?.value;

      if (!h || !w || !this.state.baseSqft) {
        this._setError(
          "Please enter your wall dimensions before saving to wishlist.",
        );
        return;
      }

      const data = {
        id: this.state.variantId,
        quantity: this.state.quantity,
        properties: {
          Height: `${h} ${this.state.unit}`,
          Width: `${w} ${this.state.unit}`,
          Area: `${parseFloat(this.state.baseSqft.toFixed(2))} Sq. Ft.`,
          ...(this.state.texture ? { Texture: this.state.texture } : {}),
        },
        ...(this.state.textureVariantId && this.state.texturePrice > 0
          ? {
              textureVariantId: this.state.textureVariantId,
              isTextureVariantMatched: this.state.isTextureVariantMatched,
            }
          : {}),
      };

      closeDrawer();
      this.callback(data);
    }
  }

  /* ------------------------------------------------------------------ */
  /* WishlistBtmWidget — By the Meter curtains                           */
  /* ------------------------------------------------------------------ */

  class WishlistBtmWidget {
    constructor(container, productUrl, callback) {
      this.container = container;
      this.productUrl = productUrl;
      this.callback = callback;

      this.el = {
        metersInput: container.querySelector("#wl-meters"),
        metersFooter: container.querySelector(".wl-meters-footer-display"),
        priceDisplay: container.querySelector(".wl-price-display"),
        errorMsg: container.querySelector(".wl-error-message"),
        errorText: container.querySelector(".wl-error-text"),
        addBtn: container.querySelector("#wl-add-to-wishlist-btn"),
        closeBtn: container.querySelector(".wl-drawer-close"),
      };

      this.state = {
        meters: 0,
        basePrice: 0,
        variantId: null,
        hasError: false,
      };

      this._debouncedFetchVariant = debounce(
        this._fetchVariant.bind(this),
        500,
      );

      this._init();
      this._fetchVariant();
    }

    _init() {
      const { metersInput, addBtn, closeBtn } = this.el;

      const intOnly = (e) => {
        const pass = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
        if (!pass.includes(e.key) && !/^[0-9]$/.test(e.key)) e.preventDefault();
      };

      metersInput?.addEventListener("keydown", intOnly);
      metersInput?.addEventListener("input", () => this._onMetersChange());

      this.container
        .querySelectorAll('input[name^="wl-option-"]')
        .forEach((r) =>
          r.addEventListener("change", (e) => {
            const pos = e.target.name.replace("wl-option-", "");
            const label = this.container.querySelector(
              `.wl-option-label-${pos}`,
            );
            if (label) label.textContent = e.target.value;
            this._debouncedFetchVariant();
          }),
        );

      closeBtn?.addEventListener("click", () => closeDrawer());
      addBtn?.addEventListener("click", () => this._submit());
    }

    _getAllSelectedOptionIds() {
      return Array.from(
        this.container.querySelectorAll('input[name^="wl-option-"]:checked'),
      )
        .map((r) => r.dataset.wlOptionId)
        .filter(Boolean);
    }

    _onMetersChange() {
      const meters = parseInt(this.el.metersInput?.value, 10) || 0;
      this.state.meters = meters;

      if (!meters || meters <= 0) {
        this._resetUI();
        return;
      }

      this._renderUI();
    }

    async _fetchVariant() {
      const optionIds = this._getAllSelectedOptionIds();
      if (!optionIds.length) return;

      const url = `${
        this.productUrl
      }?section_id=data-selected-variant&option_values=${optionIds.join(",")}`;
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const data = JSON.parse(
          doc.querySelector("[selected-variant]")?.textContent || "{}",
        );

        this.state.variantId = data.id || null;
        this.state.basePrice = (data.price || 0) / 100;
        this._renderUI();
      } catch (err) {
        console.error("[WL BTM] fetchVariant failed", err);
      }
    }

    _renderUI() {
      const { meters, basePrice, hasError } = this.state;
      const label = `${meters} meter${meters !== 1 ? "s" : ""}`;

      if (this.el.metersFooter) this.el.metersFooter.textContent = label;

      if (basePrice && this.el.priceDisplay && meters > 0) {
        const total = basePrice * meters;
        this.el.priceDisplay.innerHTML = `$${total.toLocaleString("en-IN")}`;
      }

      if (this.el.addBtn && !hasError) {
        this.el.addBtn.disabled = meters <= 0;
      }
    }

    _resetUI() {
      if (this.el.metersFooter) this.el.metersFooter.textContent = "0 meters";
      if (this.el.priceDisplay) this.el.priceDisplay.innerHTML = "$0";
      if (this.el.addBtn) this.el.addBtn.disabled = true;
      this._setError(null);
    }

    _setError(msg) {
      this.state.hasError = !!msg;
      if (this.el.errorMsg)
        this.el.errorMsg.style.display = msg ? "flex" : "none";
      if (this.el.errorText) this.el.errorText.textContent = msg || "";
      if (this.el.addBtn) this.el.addBtn.disabled = !!msg;
    }

    _submit() {
      const { meters, variantId } = this.state;

      if (!meters || meters <= 0) {
        this._setError(
          "Please enter the number of meters before saving to wishlist.",
        );
        return;
      }

      closeDrawer();
      this.callback({
        id: variantId,
        quantity: meters,
        properties: {
          Meters: `${meters} meter${meters !== 1 ? "s" : ""}`,
        },
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* WishlistHeightWidget — Stitched curtains                            */
  /* ------------------------------------------------------------------ */

  class WishlistHeightWidget {
    constructor(container, productUrl, callback) {
      this.container = container;
      this.productUrl = productUrl;
      this.callback = callback;

      // Build height → optionId map from the inches option
      const allOptionsEl = container.querySelector("#wishlist_all_options");
      const allOptions = allOptionsEl
        ? JSON.parse(allOptionsEl.textContent)
        : [];
      const inchesOption = allOptions.find((opt) => {
        const norm = opt.name.toLowerCase().replace(/[\s.]/g, "");
        return norm === "inches";
      });
      this.heightOptions = {};
      if (inchesOption) {
        inchesOption.values.forEach((v) => {
          this.heightOptions[parseInt(v.value, 10)] = v.id;
        });
      }

      this.el = {
        heightInput: container.querySelector("#wl-height"),
        heightDisplay: container.querySelector(".wl-height-display"),
        heightFooter: container.querySelector(".wl-height-footer-display"),
        priceDisplay: container.querySelector(".wl-price-display"),
        cardHeader: container.querySelector(".wl-card-header"),
        warningMsg: container.querySelector(".wl-card-warning"),
        errorMsg: container.querySelector(".wl-error-message"),
        errorText: container.querySelector(".wl-error-text"),
        addBtn: container.querySelector("#wl-add-to-wishlist-btn"),
        closeBtn: container.querySelector(".wl-drawer-close"),
        quantityDisplay: container.querySelector("#wl-quantity-display"),
        decreaseBtn: container.querySelector("#wl-quantity-decrease-btn"),
        increaseBtn: container.querySelector("#wl-quantity-increase-btn"),
      };

      this.state = {
        height: 0,
        selectedHeight: 0,
        selectedOptId: null,
        quantity: 1,
        basePrice: 0,
        variantId: null,
        hasError: false,
      };

      this._debouncedFetchVariant = debounce(
        this._fetchVariant.bind(this),
        500,
      );

      this._init();
    }

    _heightRange() {
      const heights = Object.keys(this.heightOptions)
        .map(Number)
        .sort((a, b) => a - b);
      return {
        min: heights[0] || 0,
        max: heights[heights.length - 1] || Infinity,
      };
    }

    _init() {
      const { heightInput, addBtn, closeBtn, decreaseBtn, increaseBtn } =
        this.el;

      const intOnly = (e) => {
        const pass = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"];
        if (!pass.includes(e.key) && !/^[0-9]$/.test(e.key)) e.preventDefault();
      };

      heightInput?.addEventListener("keydown", intOnly);
      heightInput?.addEventListener("input", () => this._onHeightChange());

      this.container
        .querySelectorAll('input[name^="wl-option-"]')
        .forEach((r) =>
          r.addEventListener("change", (e) => {
            const pos = e.target.name.replace("wl-option-", "");
            const label = this.container.querySelector(
              `.wl-option-label-${pos}`,
            );
            if (label) label.textContent = e.target.value;
            if (this.state.selectedOptId) this._debouncedFetchVariant();
          }),
        );

      decreaseBtn?.addEventListener("click", () => this._updateQuantity(-1));
      increaseBtn?.addEventListener("click", () => this._updateQuantity(1));
      closeBtn?.addEventListener("click", () => closeDrawer());
      addBtn?.addEventListener("click", () => this._submit());
    }

    _updateQuantity(delta) {
      const next = this.state.quantity + delta;
      if (next >= 1) {
        this.state.quantity = next;
        this._renderUI();
      }
    }

    _getAllSelectedOptionIds() {
      return Array.from(
        this.container.querySelectorAll('input[name^="wl-option-"]:checked'),
      )
        .map((r) => r.dataset.wlOptionId)
        .filter(Boolean);
    }

    _onHeightChange() {
      const height = parseInt(this.el.heightInput?.value, 10) || 0;
      this.state.height = height;

      if (!height) {
        this._resetUI();
        return;
      }

      const { min, max } = this._heightRange();

      if (height > max) {
        this._setError(`Height too large. Maximum is ${max} inches.`);
        return;
      }

      this._setError(null);

      if (height < min) {
        this.el.warningMsg && (this.el.warningMsg.style.display = "block");
        this.state.selectedHeight = min;
      } else {
        this.el.warningMsg && (this.el.warningMsg.style.display = "none");
        this.state.selectedHeight = height;
      }

      this.state.selectedOptId =
        this.heightOptions[this.state.selectedHeight] || null;
      this._debouncedFetchVariant();
      this._renderUI();
    }

    async _fetchVariant() {
      const colorIds = this._getAllSelectedOptionIds();
      const allIds = this.state.selectedOptId
        ? [...colorIds, this.state.selectedOptId]
        : colorIds;

      if (!allIds.length) return;

      const url = `${
        this.productUrl
      }?section_id=data-selected-variant&option_values=${allIds.join(",")}`;
      try {
        const res = await fetch(url);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, "text/html");
        const data = JSON.parse(
          doc.querySelector("[selected-variant]")?.textContent || "{}",
        );

        this.state.variantId = data.id || null;
        this.state.basePrice = (data.price || 0) / 100;
        this._renderUI();
      } catch (err) {
        console.error("[WL Height] fetchVariant failed", err);
      }
    }

    _renderUI() {
      const { selectedHeight, quantity, basePrice, hasError } = this.state;
      const heightLabel = `${selectedHeight} in`;

      if (this.el.heightDisplay)
        this.el.heightDisplay.textContent = `${selectedHeight} inches`;
      if (this.el.heightFooter) this.el.heightFooter.textContent = heightLabel;
      if (this.el.cardHeader)
        this.el.cardHeader.style.display =
          selectedHeight > 0 ? "block" : "none";
      if (this.el.quantityDisplay)
        this.el.quantityDisplay.textContent = quantity;

      if (basePrice && this.el.priceDisplay && selectedHeight > 0) {
        const total = basePrice * quantity;
        this.el.priceDisplay.innerHTML = `$${total.toLocaleString("en-IN")}`;
      }

      if (this.el.addBtn && !hasError) {
        this.el.addBtn.disabled = selectedHeight <= 0;
      }
    }

    _resetUI() {
      if (this.el.heightDisplay) this.el.heightDisplay.textContent = "0 inches";
      if (this.el.heightFooter) this.el.heightFooter.textContent = "0 in";
      if (this.el.priceDisplay) this.el.priceDisplay.innerHTML = "$0";
      if (this.el.cardHeader) this.el.cardHeader.style.display = "none";
      if (this.el.warningMsg) this.el.warningMsg.style.display = "none";
      if (this.el.quantityDisplay)
        this.el.quantityDisplay.textContent = this.state.quantity;
      if (this.el.addBtn) this.el.addBtn.disabled = true;
      this._setError(null);
    }

    _setError(msg) {
      this.state.hasError = !!msg;
      if (this.el.errorMsg)
        this.el.errorMsg.style.display = msg ? "flex" : "none";
      if (this.el.errorText) this.el.errorText.textContent = msg || "";
      if (this.el.addBtn) this.el.addBtn.disabled = !!msg;
    }

    _submit() {
      const { selectedHeight, quantity, variantId } = this.state;

      if (!selectedHeight) {
        this._setError(
          "Please enter your curtain height before saving to wishlist.",
        );
        return;
      }

      closeDrawer();
      this.callback({
        id: variantId,
        quantity,
        properties: {
          Height: `${selectedHeight} inches`,
        },
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Public API                                                           */
  /* ------------------------------------------------------------------ */

  window.openWishlistProductConfig = async function (
    productHandle,
    productType,
    callback,
  ) {
    console.log("[WL] openWishlistProductConfig called →", productHandle);

    if (!drawerEl) return;

    const productUrl = `/products/${productHandle}`;

    drawerEl.innerHTML = SPINNER_HTML;
    openDrawer();

    // Run the fetch and a minimum delay in parallel.
    // The delay matches the drawer slide-in duration (500ms) so the spinner
    // is always visible for the full open animation before content swaps in.
    const minDelay = new Promise((r) => setTimeout(r, 500));

    try {
      const res = await fetch(`${productUrl}?view=wishlist-config`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const html = await res.text();

      await minDelay;

      const doc = new DOMParser().parseFromString(html, "text/html");

      const cssLink = doc.querySelector('link[rel="stylesheet"]');
      if (cssLink) ensureCss(cssLink.href);

      const wrapper = document.createElement("div");
      wrapper.style.opacity = "0";
      wrapper.style.transition = "opacity 0.2s ease";
      wrapper.innerHTML = doc.body.innerHTML;
      drawerEl.innerHTML = "";
      drawerEl.appendChild(wrapper);
      requestAnimationFrame(() => (wrapper.style.opacity = "1"));

      if (productType === "BTM") {
        new WishlistBtmWidget(drawerEl, productUrl, callback);
      } else if (productType === "HEIGHT") {
        new WishlistHeightWidget(drawerEl, productUrl, callback);
      } else if (productType === "SQFT") {
        new WishlistSqftWidget(drawerEl, productUrl, callback);
      }
    } catch (err) {
      console.error("[WL] fetch failed", err);
      drawerEl.innerHTML =
        '<div style="padding:24px;color:#666;">Failed to load size selector. Please try again.</div>';
    }
  };
})();
